import { graphql } from '../../schemas/server_admin_query'

const BlockchainTransactionFields = graphql(`
  fragment BlockchainTransactionFields on BlockchainTransaction {
    id
    amount
    destination_address
    network
    source_address
    status
    tx_hash
    tx_subindex
  }
`)

const CryptoTokenFields = graphql(`
  fragment CryptoTokenFields on CryptoToken {
    address
    decimals
    network
    symbol
  }
`)

const RedeemableNetworkFields = graphql(
  `
    fragment RedeemableNetworkFields on RedeemableNetwork {
      id
      network
      deposit_token {
        ...CryptoTokenFields
      }
      settlement_token {
        ...CryptoTokenFields
      }
      fireblocks_deposit_asset_id
      fireblocks_settlement_asset_id
    }
  `,
  [CryptoTokenFields],
)

const AdminRedemptionListFields = graphql(
  `
    fragment AdminRedemptionListFields on AdminRedemption {
      id
      status
      error
      network
      deposit_total
      settlement_total
      fees_total
      commission_fee_total
      deposit_confirmed_at
      processing_at
      settled_at
      finalized_at
      created_at
      token_price
      retry_count
      redeemable_network {
        ...RedeemableNetworkFields
      }
    }
  `,
  [RedeemableNetworkFields],
)

const LiquidityProviderFields = graphql(`
  fragment LiquidityProviderFields on LiquidityProvider {
    id
    name
  }
`)

const RedemptionAllocationFields = graphql(
  `
    fragment RedemptionAllocationFields on RedemptionAllocation {
      id
      asset_id
      cause
      denomination
      total
      lpb_balance_after
      liquidity_provider {
        ...LiquidityProviderFields
      }
      liquidity_provider_id
      liquidity_provider_bucket_id
      liquidity_pool_id
      redemption_id
      idempotency_key
      blockchain_transaction {
        ...BlockchainTransactionFields
      }
      created_at
      updated_at
    }
  `,
  [LiquidityProviderFields, BlockchainTransactionFields],
)

const RedemptionProvisionFields = graphql(
  `
    fragment RedemptionProvisionFields on RedemptionProvision {
      id
      asset_id
      denomination
      total
      commission_fees
      lp_fees
      operating_fees
      effective_commission_percentage
      liquidated_total
      lpb_balance_after
      liquidity_provider {
        ...LiquidityProviderFields
      }
      liquidity_provider_id
      liquidity_provider_bucket_id
      liquidity_pool_id
      liquidity_pool_tranche_id
      redeemable_id
      redemption_id
      idempotency_key
      blockchain_transaction {
        ...BlockchainTransactionFields
      }
      created_at
      updated_at
    }
  `,
  [LiquidityProviderFields, BlockchainTransactionFields],
)

const RedemptionConversionFields = graphql(
  `
    fragment RedemptionConversionFields on RedemptionConversion {
      id
      source_asset_id
      destination_asset_id
      conversion_type
      total
      conversion_fees
      status
      unconversion
      liquidity_pool_id
      cctp_transfer_id
      redemption_id
      idempotency_key
      burn_transaction {
        ...BlockchainTransactionFields
      }
      mint_transaction {
        ...BlockchainTransactionFields
      }
      created_at
      updated_at
    }
  `,
  [BlockchainTransactionFields],
)

const RedemptionDeprovisionFields = graphql(
  `
    fragment RedemptionDeprovisionFields on RedemptionDeprovision {
      id
      asset_id
      denomination
      total
      return_total
      status
      liquidity_provider {
        ...LiquidityProviderFields
      }
      liquidity_provider_id
      liquidity_provider_bucket_id
      liquidity_pool_tranche_id
      redemption_id
      redemption_provision_id
      return_allocation_id
      return_transaction {
        ...BlockchainTransactionFields
      }
      return_transaction_id
      idempotency_key
      created_at
      updated_at
    }
  `,
  [LiquidityProviderFields, BlockchainTransactionFields],
)

const RedemptionDisbursementFields = graphql(
  `
    fragment RedemptionDisbursementFields on RedemptionDisbursement {
      id
      disbursement_type
      asset_id
      total
      amount
      status
      liquidity_provider_id
      liquidity_provider_bucket_id
      liquidity_tranche_bucket_id
      redemption_id
      idempotency_key
      blockchain_transaction {
        ...BlockchainTransactionFields
      }
      created_at
      updated_at
    }
  `,
  [BlockchainTransactionFields],
)

const LiquidityProviderBucketFields = graphql(`
  fragment LiquidityProviderBucketFields on LiquidityProviderBucket {
    id
    name
    mode
    fireblocks_vault_id
    provider_id
    balance_total
    balance_by_asset {
      asset_id
      balance
      crypto_asset {
        id
        symbol
        decimals
        native
        address
        fireblocks_asset_id
        crypto_network_id
        network_name
      }
    }
  }
`)

const LiquidityProviderWithBucketsFields = graphql(
  `
    fragment LiquidityProviderWithBucketsFields on LiquidityProvider {
      id
      name
      liquidity_provider_buckets(first: 50) {
        nodes {
          ...LiquidityProviderBucketFields
        }
      }
    }
  `,
  [LiquidityProviderBucketFields],
)

const LiquidityTrancheBucketLiteFields = graphql(
  `
    fragment LiquidityTrancheBucketLiteFields on LiquidityTrancheBucket {
      id
      bucket_id
      tranche_id
      shares
      operating_fee_percentage
      fireblocks_holdings_vault_id
      liquidity_provider {
        ...LiquidityProviderFields
      }
      liquidity_provider_bucket {
        id
        name
      }
    }
  `,
  [LiquidityProviderFields],
)

const LiquidityPoolTrancheLiteFields = graphql(
  `
    fragment LiquidityPoolTrancheLiteFields on LiquidityPoolTranche {
      id
      position
      size_limit
      utilized_capacity
      remaining_capacity
      pool_id
      liquidity_tranche_buckets(first: 10) {
        nodes {
          ...LiquidityTrancheBucketLiteFields
        }
      }
    }
  `,
  [LiquidityTrancheBucketLiteFields],
)

const LiquidityPoolLiteFields = graphql(
  `
    fragment LiquidityPoolLiteFields on LiquidityPool {
      id
      active
      active_at
      redeemable_id
      liquidity_pool_tranches(first: 10) {
        nodes {
          ...LiquidityPoolTrancheLiteFields
        }
      }
    }
  `,
  [LiquidityPoolTrancheLiteFields],
)

const RedeemableUnderlyingRedemptionFields = graphql(`
  fragment RedeemableUnderlyingRedemptionFields on RedeemableUnderlyingRedemption {
    id
    redeemable_id
    redemption_at
    total_aum
    days_until
    created_at
    updated_at
  }
`)

const AdminRedeemableFields = graphql(
  `
    fragment AdminRedeemableFields on AdminRedeemable {
      id
      name
      commission_fee_schedule
      created_at
      fee_recovery_asset
      fees_recovery_vault_id
      fireblocks_escrow_vault_id
      fireblocks_escrow_vault_sync_pos
      fireblocks_holdings_vault_id
      in_flight_redemptions
      non_success_redemptions_last_24h
      paused
      price_feed_address
      price_feed_network
      price_feed_protocol
      published
      rate_adjustment
      redemptions_last_24h
      total_redemptions
      underlying_schedule
      unmatched_reversals_paused
      updated_at
      active_liquidity_pool {
        ...LiquidityPoolLiteFields
      }
      underlying_redemptions(first: 50) {
        nodes {
          ...RedeemableUnderlyingRedemptionFields
        }
        total_count
      }
    }
  `,
  [LiquidityPoolLiteFields, RedeemableUnderlyingRedemptionFields],
)

export const query_liquidity_providers = graphql(
  `
    query query_liquidity_providers {
      liquidity_providers(first: 100) {
        nodes {
          ...LiquidityProviderWithBucketsFields
        }
        total_count
      }
    }
  `,
  [LiquidityProviderWithBucketsFields],
)

export const query_admin_redeemables = graphql(
  `
    query query_admin_redeemables {
      redeemables(first: 10) {
        nodes {
          ...AdminRedeemableFields
        }
        total_count
      }
    }
  `,
  [AdminRedeemableFields],
)

export const query_admin_redeemable = graphql(
  `
    query query_admin_redeemable($id: ID!) {
      redeemable(id: $id) {
        ...AdminRedeemableFields
      }
    }
  `,
  [AdminRedeemableFields],
)

export const query_admin_redemptions = graphql(
  `
    query query_admin_redemptions(
      $first: Int
      $after: String
      $redeemable_name: String
      $status: AdminRedemptionStatus
      $settled_start_date: ISO8601DateTime
      $settled_end_date: ISO8601DateTime
    ) {
      redemptions(
        first: $first
        after: $after
        redeemable_name: $redeemable_name
        status: $status
        settled_start_date: $settled_start_date
        settled_end_date: $settled_end_date
      ) {
        nodes {
          ...AdminRedemptionListFields
        }
        total_count
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `,
  [AdminRedemptionListFields],
)

export const query_admin_redemption = graphql(
  `
    query query_admin_redemption($id: ID!) {
      redemption(id: $id) {
        id
        status
        error
        network
        redeemable_id
        redeemable_network_id
        redeemable_network {
          ...RedeemableNetworkFields
        }
        redemption_mode
        deposit_address
        deposit_amount
        deposit_total
        deposit_confirmed_at
        deposit_transaction {
          ...BlockchainTransactionFields
        }
        deposit_transaction_hash
        deposit_disbursement_status
        deposit_disbursement_transaction_id
        settlement_total
        settlement_amount
        settlement_transaction {
          ...BlockchainTransactionFields
        }
        settled_at
        fees_total
        fees_amount
        fees_disbursement_status
        fees_disbursement_transaction_id
        commission_fee_total
        commission_fee_percent
        commission_fee_percent_max
        commission_fee_expenses
        commission_fee_lp
        commission_fee_operating
        operating_fee_percentage
        token_price
        total_aum
        post_redemption_aum_percent
        net_settlement_total_min
        retry_count
        source_address
        ip_address
        pool_id
        callback_url
        execution_plan
        created_at
        processing_at
        finalized_at
        recovery_required_at
        expires_at
        upcoming_underlying_at
        webhook_enqueued_at
        successful_callbacked_at
        updated_at
        reversal_transaction {
          ...BlockchainTransactionFields
        }
        reversal_transaction_id
        fee_recovery_transaction {
          ...BlockchainTransactionFields
        }
        fee_recovery_transaction_id
        onchain_approval_transaction {
          ...BlockchainTransactionFields
        }
        onchain_approval_transaction_id
        onchain_pull_transaction {
          ...BlockchainTransactionFields
        }
        onchain_pull_transaction_id
        onchain_pull_close_transaction {
          ...BlockchainTransactionFields
        }
        onchain_pull_close_transaction_id
        redemption_allocations(first: 100) {
          nodes {
            ...RedemptionAllocationFields
          }
          total_count
        }
        redemption_provisions(first: 100) {
          nodes {
            ...RedemptionProvisionFields
          }
          total_count
        }
        redemption_conversions(first: 100) {
          nodes {
            ...RedemptionConversionFields
          }
          total_count
        }
        redemption_deprovisions(first: 100) {
          nodes {
            ...RedemptionDeprovisionFields
          }
          total_count
        }
        redemption_disbursements(first: 100) {
          nodes {
            ...RedemptionDisbursementFields
          }
          total_count
        }
      }
    }
  `,
  [
    RedeemableNetworkFields,
    BlockchainTransactionFields,
    RedemptionAllocationFields,
    RedemptionProvisionFields,
    RedemptionConversionFields,
    RedemptionDeprovisionFields,
    RedemptionDisbursementFields,
  ],
)
